/**
 * PauseMenu - Overlay for pausing gameplay
 *
 * Features:
 * - Semi-transparent overlay (80% opacity)
 * - Resume, Restart Level, and Quit to Menu buttons
 * - Sound and Music toggle icons
 * - Integrates with SaveSystem for settings persistence
 *
 * Per PRD section 5.2 and Task #19 specifications
 */

import Phaser from 'phaser';
import {
  COLORS,
  GAME_WIDTH,
  GAME_HEIGHT,
  FONTS,
  TEXT_SIZES,
  SPACING,
} from '../config';
import { Button } from './Button';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';

export interface PauseMenuConfig {
  /** Callback when Resume is clicked */
  onResume: () => void;
  /** Callback when Restart Level is clicked */
  onRestart: () => void;
  /** Callback when Quit to Menu is clicked */
  onQuit: () => void;
}

/**
 * PauseMenu displays a pause overlay with controls
 *
 * Usage:
 *   const pauseMenu = new PauseMenu(scene, {
 *     onResume: () => this.togglePause(),
 *     onRestart: () => this.restartLevel(),
 *     onQuit: () => this.quitToMenu(),
 *   });
 *   pauseMenu.show();
 *   pauseMenu.hide();
 */
export class PauseMenu extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.Rectangle;
  private titleText!: Phaser.GameObjects.Text;
  private resumeButton!: Button;
  private restartButton!: Button;
  private quitButton!: Button;
  private soundToggle!: Button;
  private musicToggle!: Button;
  private soundEnabled: boolean = true;
  private musicEnabled: boolean = true;
  private isDestroyed: boolean = false;

  private config: PauseMenuConfig;

  constructor(scene: Phaser.Scene, config: PauseMenuConfig) {
    super(scene, 0, 0);

    this.config = config;

    // Load current settings from SaveSystem
    const settings = saveSystem.getSettings();
    this.soundEnabled = settings.soundEnabled;
    this.musicEnabled = settings.musicEnabled;

    this.createBackground();
    this.createTitle();
    this.createButtons();
    this.createAudioToggles();

    // Start hidden
    this.setVisible(false);

    // Set high depth to render above everything
    this.setDepth(100);

    scene.add.existing(this);
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the semi-transparent background overlay
   */
  private createBackground(): void {
    this.background = this.scene.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      COLORS.bgOverlay,
      0.8
    );
    this.add(this.background);

    // Make background interactive to block clicks through to game
    this.background.setInteractive();
  }

  /**
   * Creates the "PAUSED" title text
   */
  private createTitle(): void {
    this.titleText = this.scene.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT * 0.25,
      'PAUSED',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.display}px`,
        color: '#FFFFFF',
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);

    this.add(this.titleText);
  }

  /**
   * Creates the main action buttons
   */
  private createButtons(): void {
    const buttonWidth = 220;
    const buttonHeight = 56;
    const buttonSpacing = SPACING.space4;
    const startY = GAME_HEIGHT * 0.42;

    // Resume button (primary)
    this.resumeButton = new Button(this.scene, {
      x: GAME_WIDTH / 2,
      y: startY,
      text: 'Resume',
      variant: 'primary',
      width: buttonWidth,
      height: buttonHeight,
      onClick: () => this.config.onResume(),
    });
    this.add(this.resumeButton);

    // Restart Level button (secondary)
    this.restartButton = new Button(this.scene, {
      x: GAME_WIDTH / 2,
      y: startY + buttonHeight + buttonSpacing,
      text: 'Restart Level',
      variant: 'secondary',
      width: buttonWidth,
      height: 48,
      onClick: () => this.config.onRestart(),
    });
    this.add(this.restartButton);

    // Quit to Menu button (secondary)
    this.quitButton = new Button(this.scene, {
      x: GAME_WIDTH / 2,
      y: startY + (buttonHeight + buttonSpacing) * 2,
      text: 'Quit to Menu',
      variant: 'secondary',
      width: buttonWidth,
      height: 48,
      onClick: () => this.config.onQuit(),
    });
    this.add(this.quitButton);
  }

  /**
   * Creates the sound and music toggle buttons
   */
  private createAudioToggles(): void {
    const toggleY = GAME_HEIGHT * 0.75;
    const toggleSpacing = SPACING.space6;

    // Sound toggle
    this.soundToggle = new Button(this.scene, {
      x: GAME_WIDTH / 2 - toggleSpacing / 2 - 24,
      y: toggleY,
      icon: this.soundEnabled ? 'icon-sound-on' : 'icon-sound-off',
      variant: 'icon',
      onClick: () => this.toggleSound(),
    });
    this.add(this.soundToggle);

    // Music toggle
    this.musicToggle = new Button(this.scene, {
      x: GAME_WIDTH / 2 + toggleSpacing / 2 + 24,
      y: toggleY,
      icon: this.musicEnabled ? 'icon-music-on' : 'icon-music-off',
      variant: 'icon',
      onClick: () => this.toggleMusic(),
    });
    this.add(this.musicToggle);

    // Labels under the toggles
    const labelStyle = {
      fontFamily: FONTS.body,
      fontSize: `${TEXT_SIZES.caption}px`,
      color: '#A0A0B0',
    };

    const soundLabel = this.scene.add.text(
      GAME_WIDTH / 2 - toggleSpacing / 2 - 24,
      toggleY + 36,
      'Sound',
      labelStyle
    ).setOrigin(0.5);
    this.add(soundLabel);

    const musicLabel = this.scene.add.text(
      GAME_WIDTH / 2 + toggleSpacing / 2 + 24,
      toggleY + 36,
      'Music',
      labelStyle
    ).setOrigin(0.5);
    this.add(musicLabel);
  }

  // ===========================================================================
  // TOGGLE HANDLERS
  // ===========================================================================

  /**
   * Toggles sound effects on/off
   */
  private toggleSound(): void {
    // Use audioSystem to toggle (handles SaveSystem and plays feedback)
    this.soundEnabled = audioSystem.toggleSound();

    // Update icon
    this.updateSoundIcon();
  }

  /**
   * Toggles music on/off
   */
  private toggleMusic(): void {
    // Use audioSystem to toggle
    this.musicEnabled = audioSystem.toggleMusic();

    // Update icon
    this.updateMusicIcon();
  }

  /**
   * Updates the sound toggle icon based on current state
   */
  private updateSoundIcon(): void {
    // Remove old button and create new one with updated icon
    const x = this.soundToggle.x;
    const y = this.soundToggle.y;
    this.soundToggle.destroy();

    this.soundToggle = new Button(this.scene, {
      x,
      y,
      icon: this.soundEnabled ? 'icon-sound-on' : 'icon-sound-off',
      variant: 'icon',
      onClick: () => this.toggleSound(),
    });
    this.add(this.soundToggle);
  }

  /**
   * Updates the music toggle icon based on current state
   */
  private updateMusicIcon(): void {
    // Remove old button and create new one with updated icon
    const x = this.musicToggle.x;
    const y = this.musicToggle.y;
    this.musicToggle.destroy();

    this.musicToggle = new Button(this.scene, {
      x,
      y,
      icon: this.musicEnabled ? 'icon-music-on' : 'icon-music-off',
      variant: 'icon',
      onClick: () => this.toggleMusic(),
    });
    this.add(this.musicToggle);
  }

  // ===========================================================================
  // VISIBILITY
  // ===========================================================================

  /**
   * Shows the pause menu with fade-in animation
   */
  show(): void {
    if (this.isDestroyed) return;

    // Refresh settings state in case they changed
    const settings = saveSystem.getSettings();
    this.soundEnabled = settings.soundEnabled;
    this.musicEnabled = settings.musicEnabled;
    this.updateSoundIcon();
    this.updateMusicIcon();

    this.setVisible(true);
    this.setAlpha(0);

    this.scene?.tweens?.add({
      targets: this,
      alpha: 1,
      duration: 150,
      ease: 'Power2',
    });
  }

  /**
   * Hides the pause menu with fade-out animation
   */
  hide(): void {
    if (this.isDestroyed) return;

    this.scene?.tweens?.add({
      targets: this,
      alpha: 0,
      duration: 100,
      ease: 'Power2',
      onComplete: () => {
        this.setVisible(false);
      },
    });
  }

  /**
   * Immediately hides without animation
   */
  hideImmediate(): void {
    this.setVisible(false);
    this.setAlpha(1);
  }

  /**
   * Returns whether the menu is currently visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.scene?.tweens?.killTweensOf(this);
    super.destroy(fromScene);
  }
}
